import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CheckCircle, CheckCircle2, Users, Calendar } from "lucide-react";
import { formatRewardAmount } from "@/lib/textUtils";

export interface HireFormData {
  hiredDate: string;
  agreedSalary: string;
}

interface HiredConfirmationModalProps {
  isOpen: boolean;
  candidateName: string | null;
  jobTitle?: string;
  rewardAmount?: number;
  bonusAmount?: number;
  hasBonus?: boolean;
  onConfirm: (formData: HireFormData) => void;
  onCancel: () => void;
  isProcessing?: boolean;
  error?: string | null;
  success?: boolean;
}

export function HiredConfirmationModal({ 
  isOpen, 
  candidateName, 
  jobTitle,
  rewardAmount,
  bonusAmount,
  hasBonus,
  onConfirm, 
  onCancel,
  isProcessing = false,
  error = null,
  success = false
}: HiredConfirmationModalProps) {
  const [formData, setFormData] = useState<HireFormData>({
    hiredDate: new Date().toISOString().split('T')[0], // Default to today
    agreedSalary: ''
  });
  const [errors, setErrors] = useState<Partial<HireFormData>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        hiredDate: new Date().toISOString().split('T')[0],
        agreedSalary: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Partial<HireFormData> = {};
    
    if (!formData.hiredDate) {
      newErrors.hiredDate = 'Hired date is required';
    }
    
    if (!formData.agreedSalary.trim()) {
      newErrors.agreedSalary = 'Agreed salary is required';
    } else if (isNaN(Number(formData.agreedSalary.replace(/[,$]/g, '')))) {
      newErrors.agreedSalary = 'Please enter a valid salary amount';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm(formData);
    }
  };

  const handleCancel = () => {
    if (isProcessing || success) return; // Prevent closing when processing or showing success
    
    setFormData({
      hiredDate: new Date().toISOString().split('T')[0],
      agreedSalary: ''
    });
    setErrors({});
    onCancel();
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && !isProcessing && !success && handleCancel()}>
      <AlertDialogContent 
        overlayClassName="!z-[9998]"
        className="sm:max-w-lg !z-[9999]"
        {...((isProcessing || success) ? {
          onPointerDownOutside: (e: Event) => e.preventDefault(),
          onEscapeKeyDown: (e: KeyboardEvent) => e.preventDefault()
        } : {})}
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <AlertDialogTitle className="space-grotesk text-green-700 text-left">
                Confirm Hire Decision
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="text-left">
            You are about to mark <strong>{candidateName}</strong> as <strong>HIRED</strong> for the position: <strong>{jobTitle}</strong>.
          </AlertDialogDescription>
          
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <Users className="w-5 h-5 text-blue-600 mt-0.5" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-blue-900 mb-1">This action will automatically:</p>
                <ul className="text-blue-800 space-y-1">
                  <li>• Reject all other candidates for this position</li>
                  <li>• Create a reward for the referrer</li>
                  {rewardAmount && (
                    <li className="flex items-center gap-1">
                      • Award <strong className="text-orange-600">R{formatRewardAmount(rewardAmount)}</strong>
                      {hasBonus && bonusAmount && (
                        <span className="text-green-600">
                          {` + R${formatRewardAmount(bonusAmount)} bonus`}
                        </span>
                      )}
                    </li>
                  )}
                  {hasBonus && bonusAmount && rewardAmount && (
                    <li className="flex items-center gap-1 border-t pt-1 mt-1">
                      • <strong className="text-green-600">Total: R{formatRewardAmount(rewardAmount + bonusAmount)}</strong>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </AlertDialogHeader>

        {/* Form Fields */}
        <div className="space-y-4 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                  <span className="text-red-600 text-xs font-bold">!</span>
                </div>
                <div className="text-sm text-red-800">
                  <p className="font-medium">Error Processing Hire</p>
                  <p className="text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          {success && !error && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div className="text-sm text-green-800">
                  <p className="font-medium">Candidate hired successfully!</p>
                  <p className="text-green-600">Rewards have been processed. Closing in a moment...</p>
                </div>
              </div>
            </div>
          )}
          
          {isProcessing && !success && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Processing hire...</p>
                  <p className="text-blue-600">This may take a few moments while we update the system.</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="hiredDate" className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Hired Date *
            </Label>
            <Input
              id="hiredDate"
              type="date"
              value={formData.hiredDate}
              onChange={(e) => setFormData(prev => ({ ...prev, hiredDate: e.target.value }))}
              className={errors.hiredDate ? "border-red-500" : ""}
              disabled={isProcessing || success}
            />
            {errors.hiredDate && (
              <p className="text-sm text-red-600">{errors.hiredDate}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="agreedSalary" className="text-sm font-medium flex items-center gap-2">
              Agreed Salary *
            </Label>
            <Input
              id="agreedSalary"
              type="text"
              placeholder="e.g., 120,000"
              value={formData.agreedSalary}
              onChange={(e) => setFormData(prev => ({ ...prev, agreedSalary: e.target.value }))}
              className={errors.agreedSalary ? "border-red-500" : ""}
              disabled={isProcessing || success}
            />
            {errors.agreedSalary && (
              <p className="text-sm text-red-600">{errors.agreedSalary}</p>
            )}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button
              variant="secondary"
              onClick={handleCancel}
              className="space-grotesk"
              disabled={isProcessing || success}
            >
              Cancel
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              onClick={handleConfirm}
              variant="primary"
              disabled={isProcessing || success}
            >
              {success ? "Success!" : isProcessing ? "Processing..." : "Confirm Hire"}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
